import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useLastItemRef } from '../../../hooks/useLastItemRef'
import useMembershipInfo from '../../../hooks/useMembershipInfo'
import noItemsImg from '../assets/noitems.svg'
import draftsPlaceholderImg from '../../../assets/draftsPlaceholder.svg'
import lockIcon from '../../../assets/lock.svg'
import membershipIcon from '../../../assets/badge_membership_on.png'
import { useDispatch } from 'react-redux'
import { setShowMembershipModal } from '../../../redux/designerReducers/designerReducer'
import DraftsLimitModal from '../../../components/DraftsLimitModal'

const NoItemsFoundContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  color: #c4c4c4;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 20px;

  @media screen and (max-width: 480px) {
    padding: 0;
  }
`

const NoItemsImage = styled.img`
  width: 467px;
  height: 303px;
  margin-top: 75px;

  @media screen and (max-width: 480px) {
    width: 70vw;
    height: auto;
    margin-top: 10px;
  }
`

const NoItemsText = styled.p`
  padding-top: 33px;
  text-align: center;
  font-weight: 500;
  font-size: 18px;
  text-align: center;
  letter-spacing: 0.01em;
  color: #8b8b8b;

  @media screen and (max-width: 600px) {
    font-size: 14px;
    line-height: 17px;
    padding: 0;
    margin-bottom: 20px;
  }
`

const NonMemberLimitContainer = styled.div`
  width: 16.7vmax;
  height: 16.7vmax;
  border-radius: 16px;
  background-size: cover;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.2);
  font-weight: 500;
  font-size: 24px;
  line-height: 29px;
  color: #5c5c5c;
  position: relative;

  & img {
    margin-bottom: 10px;
  }

  @media (max-width: 820px) {
    width: 31vw;
    height: 31vw;
  }

  @media screen and (max-width: 630px) {
    width: 95vw;
    height: 95vw;
  }
`

const LockContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`

const SubscribePromptContainer = styled.div`
  position: absolute;
  bottom: 100px;
  width: 100%;
  text-align: center;

  & p {
    font-weight: 400;
    font-size: 20px;
    line-height: 150%;
    color: #000000;

    & strong {
      color: #ff5e6d;
    }

    @media screen and (max-width: 600px) {
      font-size: 14px;
      line-height: 21px;
      letter-spacing: 0px;
    }
  }

  @media screen and (max-width: 600px) {
    position: relative;
    bottom: auto;
  }
`

const UnlockButton = styled.button`
  background: #ff5e6d;
  border-radius: 115px;
  max-width: 80vw;
  width: 480px;
  height: 48px;
  position: relative;
  text-align: center;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.02em;
  color: #ffffff;
  border: none;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }

  @media screen and (max-width: 600px) {
    height: 40px;
    font-size: 14px;
    font-weight: 700;
    line-height: 17px;
    letter-spacing: 0.02em;
  }
`

const UnlockCircle = styled.div`
  position: absolute;
  top: 5px;
  left: 6px;
  background: white;
  height: 37px;
  width: 36px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  & img {
    height: 29px;
  }

  @media screen and (max-width: 600px) {
    left: 2px;
    top: 2px;
    height: 36px;
  }
`

const NON_MEMBER_DRAFTS_LIMIT = -1

const DraftsFeed = ({
  data,
  onFetch,
  onClick,
  card,
  loading,
  error,
  ...props
}) => {
  const dispatch = useDispatch()
  const membershipInfo = useMembershipInfo()
  const lastFeedItem = useLastItemRef(onFetch)
  const [showLimitModal, setShowLimitModal] = useState(false)

  const dataSource = useMemo(() => {
    if (membershipInfo.isMember) return data

    return []
  }, [membershipInfo.isMember, data])

  if (!data) return <></>

  const subscibePrompt = !membershipInfo.isMember ? (
    <SubscribePromptContainer>
      <UnlockButton onClick={() => dispatch(setShowMembershipModal(true))}>
        <UnlockCircle>
          <img src={membershipIcon} alt="" />
        </UnlockCircle>
        Unlock Unlimited Drafts
      </UnlockButton>
      <p>
        Draft is only available to members! Become a member and never worry
        about losing a design!
      </p>
    </SubscribePromptContainer>
  ) : null

  if (data && data.length === 0 && error === 'empty') {
    return (
      <NoItemsFoundContainer>
        <NoItemsImage src={noItemsImg} alt="" />
        <NoItemsText>No Draft yet</NoItemsText>
        {subscibePrompt}
      </NoItemsFoundContainer>
    )
  }

  const shouldShowNonMemberCard = !membershipInfo.isMember

  return (
    <>
      {dataSource.map((item, index) => {
        const Card = card
        return (
          <Card
            key={index}
            data={item}
            refFn={lastFeedItem}
            onClick={onClick}
            {...props}
          />
        )
      })}
      {shouldShowNonMemberCard && (
        <NonMemberLimitContainer onClick={() => setShowLimitModal(true)}>
          <img src={draftsPlaceholderImg} alt="" />
          <span>{data.length}+</span>
          <LockContainer>
            <img src={lockIcon} alt="" />
          </LockContainer>
        </NonMemberLimitContainer>
      )}
      {subscibePrompt}
      {showLimitModal && (
        <DraftsLimitModal onClose={() => setShowLimitModal(false)} />
      )}
    </>
  )
}

export default DraftsFeed
